<!-- Results chart -->
<div class="card mb-3" id="efficient-frontier-chart-results">
  <div class="card-body">
    <div class="card-head">
      <div class="card-title">Results<app-info infoIndex="18"></app-info></div>
    </div>
    <div class="row">
      <div class="col-md-12" style="position: relative">
        @if (loaded()) {
          <highcharts-chart
            [options]="chartOptions"
            [constructorType]="chartConstructor"
            (chartInstance)="setChartInstance($event)"
            style="width: 100%; height: auto; display: block"
          ></highcharts-chart>
        }
      </div>
    </div>
  </div>
</div>

<!-- Slider + table -->
<div class="card mb-3" id="efficient-frontier-chart-results-table">
  <div class="card-body">
    <div class="efficient-toggle">
      <mat-slide-toggle
        color="primary"
        [(ngModel)]="showUnconstrainted"
        name="show-unconstrainted"
        (ngModelChange)="onConstraintToggleChange()"
        >Unconstrained</mat-slide-toggle
      >
      <mat-slide-toggle
        class="ms-4"
        color="primary"
        [(ngModel)]="showStocks"
        name="show-stock"
        (ngModelChange)="onStockToggleChange()"
        >Show Equities</mat-slide-toggle
      >
      <mat-slide-toggle
        class="ms-4"
        color="primary"
        [(ngModel)]="showBenchmark"
        name="show-benchmark"
        (ngModelChange)="onBenchmarkToggleChange()"
        >Show Benchmark</mat-slide-toggle
      >

      <mat-slide-toggle
        class="float-end"
        color="primary"
        [(ngModel)]="sliderLocked"
        name="slider-locked"
        [disabled]="viewSavedCustomTrade"
        [matTooltip]="
          viewSavedCustomTrade
            ? 'Slider is disabled while the saved custom trade view is active'
            : ''
        "
        (ngModelChange)="onLockChange()"
        >{{ sliderLocked ? "Unlock" : "Lock" }} slider</mat-slide-toggle
      >

      <mat-slide-toggle
        class="float-end me-3"
        color="primary"
        [(ngModel)]="viewSavedCustomTrade"
        name="view-saved-custom-trade"
        [disabled]="!hasSavedCustomTrade"
        [matTooltip]="
          hasSavedCustomTrade
            ? 'Switch between the optimal slider portfolio and the saved Next Best Trade scenario'
            : 'Save a Next Best Trade to enable this view'
        "
        (ngModelChange)="onCustomTradeViewChange()"
      >
        View:
        {{
          viewSavedCustomTrade
            ? "Saved Custom Trade"
            : "Optimal Slider Portfolio"
        }}
      </mat-slide-toggle>
    </div>

    @if (!viewSavedCustomTrade) {
      <div class="row align-items-center mb-4">
        <div class="col-1 text-end">Min</div>
        <div class="col-10">
          <div class="slider-container" [hidden]="!sliderLoaded">
            <mat-slider
              #slider
              [max]="sliderMax"
              [min]="0"
              [step]="1"
              [showTickMarks]="true"
              color="primary"
              class="w-100"
              [displayWith]="formatLabel"
              [disabled]="sliderLocked"
            >
              <input
                matSliderThumb
                [value]="sliderVal"
                [(ngModel)]="sliderVal"
                [disabled]="sliderLocked"
                (valueChange)="onSliderChange($event)"
              />
            </mat-slider>
            @if (fixedPointTangencyPoint !== null) {
              <div
                class="fixed-point-indicator cursor-pointer"
                [style.left]="getFixedPointPosition()"
                (click)="onFixedPointClick(fixedPointTangencyPoint)"
              >
                <div class="fixed-point-label">Tangency PF</div>
              </div>
            }
            @if (fixedMaxSharpePoint !== null) {
              <div
                class="fixed-point-indicator bottom-indicator cursor-pointer"
                [style.left]="getFixedMaxSharpePointPosition()"
                (click)="onFixedPointClick(fixedMaxSharpePoint)"
              >
                <div class="fixed-point-label">Max Sharpe</div>
              </div>
            }
          </div>
        </div>
        <div class="col-1">Max</div>
      </div>
    }

    <app-info infoIndex="19"></app-info>
    @if (sliderData.length) {
      <table class="table">
        <thead>
          <tr>
            <th>Asset</th>
            <th class="text-end">Expected Return</th>
            <th class="text-end">Historical Return</th>
            <th class="text-end">Sharpe Ratio</th>
            <th class="text-end">Volatility</th>
            <th class="text-end">Allocation</th>
            <th class="text-end">Min Weight</th>
            <th class="text-end">Max Weight</th>
          </tr>
        </thead>
        <tbody>
          @for (item of sliderData; track item.ticker) {
            <tr>
              <td>
                <span
                  class="underline-on-hover"
                  (click)="onCompanyClick(item.ticker)"
                >
                  {{ item.companyName }}
                </span>
              </td>
              <td class="text-end">
                {{ item.expectedReturn * 100 | number: "1.2-2" }}%
              </td>
              <td class="text-end">
                {{ item.historicalReturn * 100 | number: "1.2-2" }}%
              </td>
              <td class="text-end">{{ item.sharpeRatio | number: "1.2-2" }}</td>
              <td class="text-end">
                {{ item.volatility * 100 | number: "1.2-2" }}%
              </td>
              <td class="text-end">{{ item.allocation | number: "1.2-2" }}%</td>
              <td class="text-end">
                {{
                  item.minWeight ? (item.minWeight | number: "1.2-2") + "%" : ""
                }}
              </td>
              <td class="text-end">
                {{
                  item.maxWeight ? (item.maxWeight | number: "1.2-2") + "%" : ""
                }}
              </td>
            </tr>
          }
        </tbody>
        <tfoot>
          <tr>
            <td>Portfolio</td>
            <td class="text-end">
              {{ sliderPortfolioData.y | number: "1.2-2" }}%
            </td>
            <td class="text-end"></td>
            <td class="text-end">
              {{ sliderPortfolioData.sharpeRatio | number: "1.2-2" }}
            </td>
            <td class="text-end">
              {{ sliderPortfolioData.x | number: "1.2-2" }}%
            </td>
            <td class="text-end">100.00%</td>
            <td class="text-end"></td>
            <td class="text-end"></td>
          </tr>
        </tfoot>
      </table>
    }

    <app-next-best-trade
      [sliderVal]="sliderVal"
      [baselineMetrics]="baselineMetrics"
      [selectedPortfolioMetrics]="selectedPortfolioMetrics"
      [sliderAllocations]="sliderData"
      [assetList]="optimWeights"
      (tradePointsChanged)="onTradePointsChanged($event)"
      (customTradeSaved)="onCustomTradeSaved($event)"
    >
    </app-next-best-trade>
  </div>
</div>

<!-- Transition Map -->
@if (areaChartData.length > 0) {
  <div class="card mb-3" id="efficient-frontier-chart-transition-map">
    <div class="card-body">
      <div class="card-head">
        <div class="card-title">
          Transition Map<app-info infoIndex="20"></app-info>
        </div>
      </div>
      <app-area-chart
        [chartSeries]="areaChartData"
        [chartColors]="areaChartColors"
        [volatilityLine]="volatilityLine"
        [minVolatility]="minVolatility"
        [maxVolatility]="maxVolatility"
        [volatilityLineText]="optim"
        [sliderVolatility]="sliderVolatility"
      >
      </app-area-chart>
    </div>
  </div>
}

<!-- Portfolio Weights + Portfolio Returns -->
@if (sliderData.length) {
  <div class="row g-3 mb-3">
    <div
      class="col-md-6 pagebreak"
      id="efficient-frontier-chart-summary-weights-donut"
    >
      <div class="card h-100">
        <div class="card-body">
          <div class="card-head">
            <div class="card-title">
              Portfolio Weights<app-info infoIndex="21"></app-info>
            </div>
          </div>
          <app-donut-chart
            [data]="sliderData"
            [type]="'allocation'"
          ></app-donut-chart>
        </div>
      </div>
    </div>
    <div class="col-md-6" id="efficient-frontier-chart-summary-returns-donut">
      <div class="card h-100">
        <div class="card-body">
          <div class="card-head">
            <div class="card-title">
              Portfolio Returns<app-info infoIndex="22"></app-info>
            </div>
          </div>
          <app-donut-chart [data]="sliderData"></app-donut-chart>
        </div>
      </div>
    </div>
  </div>
}
